import Button from 'components/Basic/Button';
import { clearToolModeHandlers, registerToolModeHandlers } from 'components/tool/Editor/ToolRegistry';
import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from 'store';
import { selectIsAnyToolActionActive, TOOL_ACTION } from 'store/selectors/toolSelector';
import { toggleDeleteFacetsMode } from 'store/slices/ToolSlice';
import useCreateDeleteFacet from 'hooks/api/facet/useCreateDeleteFacet';
import { changePanel } from 'store/slices/RoofDataSlice/roofDataSlice';
import { selectIsRecomputingDsm } from 'store/slices/DsmShadingSlice/selectors';
import useQueriesRunningInBackGround from 'hooks/useQueriesRunningInBackGround';

export default function DeleteFacetToggle() {
	const allFacets = useSelector((state: RootState) => state.roofData.data.allRoofSegs);
	const { deleteFacetMode, deletedRoofIndexes } = useSelector((state: RootState) => state.toolNewPostions.data);
	const { uuid, panel, designType } = useSelector((state: RootState) => state.roofData.data);
	const isSolarDesign = designType === 'SOLAR';

	const isAnyOtherToolAction = useSelector(selectIsAnyToolActionActive);
	const isRecomputing = useSelector(selectIsRecomputingDsm);
	const queriesRunningInBackground = useQueriesRunningInBackGround();
	const dispatch = useDispatch<AppDispatch>();
	const createDeleteFacetMutation = useCreateDeleteFacet();
	const noFacetToDelete = !allFacets.length;
	const disable = noFacetToDelete || (!deleteFacetMode && isAnyOtherToolAction) || isRecomputing || queriesRunningInBackground;

	useEffect(() => {
		const cancelHandler = () => {
			dispatch(toggleDeleteFacetsMode({ enable: false }));
		};

		const doneHandler = async () => {
			if (!deletedRoofIndexes || deletedRoofIndexes.length === 0) {
				dispatch(toggleDeleteFacetsMode({ enable: false }));
				return;
			}

			await createDeleteFacetMutation.mutateAsync();
			await dispatch(changePanel({ uuid, panelKey: panel.key, designType })).unwrap();

			dispatch(toggleDeleteFacetsMode({ enable: false }));
		};

		registerToolModeHandlers(TOOL_ACTION.DELETE_FACET, {
			cancel: cancelHandler,
			done: doneHandler,
			cancelConfirmMessage: 'Are you sure you want to discard deleted facets?',
			doneConfirmMessage: 'Are you sure you want to delete the selected facets?',
		});

		return () => {
			clearToolModeHandlers(TOOL_ACTION.DELETE_FACET);
		};
	}, [dispatch, createDeleteFacetMutation, deletedRoofIndexes, uuid, panel?.key, designType]);

	return (
		<Button
			id={isSolarDesign ?'delete-facet-mode' : 'delete-facet-mode-roof'}
			className={`${deleteFacetMode ? 'active' : ''} btn-tools`}
			style={{
				background: `${deleteFacetMode ? 'var(--toolActiveBgColor)' : 'var(--defaultBgColor)'}`,
				color: `${deleteFacetMode ? 'var(--primary)' : 'var(--darkAccentColor)'}`,
			}}
			disabled={disable}
			onClick={() => dispatch(toggleDeleteFacetsMode({ enable: !deleteFacetMode }))}
			tooltipContent='Delete Facets'
		>
			<svg width="21" height="22.91" viewBox="0 0 22 24" fill="none" xmlns="http://www.w3.org/2000/svg">
				<path d="M8.5 10.7778V18.1111M13.5 10.7778V18.1111M1 5.88889H21M19.75 5.88889L18.6662 20.7291C18.6214 21.3458 
						18.3391 21.923 17.8764 22.3444C17.4137 22.7657 16.8048 23 16.1725 23H5.8275C5.19517 23 4.58633 22.7657 4.12361 
						22.3444C3.66088 21.923 3.37865 21.3458 3.33375 20.7291L2.25 5.88889H19.75ZM14.75 5.88889V2.22222C14.75 1.89807 
						14.6183 1.58719 14.3839 1.35798C14.1495 1.12877 13.8315 1 13.5 1H8.5C8.16848 1 7.85054 1.12877 7.61612 1.35798C7.3817 
						1.58719 7.25 1.89807 7.25 2.22222V5.88889H14.75Z"
				// eslint-disable-next-line no-constant-condition
				stroke={deleteFacetMode ? 'var(--primary)' : 'var(--defaultIconColor)'}
				strokeWidth="1.60573"
				strokeLinecap="round"
				strokeLinejoin="round" />
			</svg>
		</Button>
	);

}